import { isConsentManaged } from '@/lib/consent/cookieYes';
const CONSENT_COOKIE = 'cookieyes-consent';
/**
 * CookieYes's consent cookie as a map, e.g. `{ consent: 'yes', action: 'yes', analytics: 'no' }`,
 * or null when there is none. The value is `key:value` pairs joined by commas, unencoded.
 */
export function parseCookieYesConsent(cookieHeader) {
    for (const part of cookieHeader.split(';')) {
        const eq = part.indexOf('=');
        if (eq < 0 || part.slice(0, eq).trim() !== CONSENT_COOKIE)
            continue;
        const consent = {};
        for (const pair of part.slice(eq + 1).split(',')) {
            const colon = pair.indexOf(':');
            if (colon <= 0)
                continue;
            consent[pair.slice(0, colon).trim()] = pair.slice(colon + 1).trim();
        }
        return consent;
    }
    return null;
}
function globalPrivacyControl() {
    return navigator.globalPrivacyControl === true;
}
/** Reads the current state straight from the browser. */
export function readConsentState() {
    if (!isConsentManaged())
        return { analytics: true, performance: true };
    if (globalPrivacyControl())
        return { analytics: false, performance: false };
    const consent = parseCookieYesConsent(document.cookie);
    return {
        analytics: consent?.analytics === 'yes',
        performance: consent?.performance === 'yes',
    };
}
let current = null;
const listeners = new Set();
let watching = false;
/** The cached state; cheap enough for per-event checks. */
export function consentState() {
    current ??= readConsentState();
    return current;
}
export function hasConsent(category) {
    return consentState()[category];
}
/** Re-read the cookie and notify listeners when anything changed. */
export function refreshConsent() {
    const previous = consentState();
    const next = readConsentState();
    current = next;
    if (previous.analytics !== next.analytics || previous.performance !== next.performance) {
        for (const listener of listeners)
            listener(next);
    }
}
export function onConsentChange(listener) {
    listeners.add(listener);
    return () => listeners.delete(listener);
}
/**
 * Re-read the choice whenever it may have changed: CookieYes's own update event (on the public
 * pages, where the banner runs), and on returning to the tab, which is how a change made in the
 * preference centre in another tab reaches a clinical screen. Idempotent.
 */
export function watchConsent() {
    if (watching)
        return;
    watching = true;
    document.addEventListener('cookieyes_consent_update', refreshConsent);
    window.addEventListener('focus', refreshConsent);
    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible')
            refreshConsent();
    });
}
